import type { AgentAction, AgentActionResult, AgentModule } from './actionTypes'
import type { CrudModuleBridge } from './bridgeRegistry'

interface RunnerDeps {
  activeModule: AgentModule | null
  getBridge: (module: AgentModule) => CrudModuleBridge | null
}

function pickModule(action: AgentAction, activeModule: AgentModule | null): AgentModule | null {
  if (action.module) return action.module
  return activeModule
}

export async function runAgentActions(actions: AgentAction[], deps: RunnerDeps): Promise<AgentActionResult[]> {
  const results: AgentActionResult[] = []

  for (const action of actions) {
    const module = pickModule(action, deps.activeModule)
    if (!module) {
      results.push({
        action,
        status: 'needs_input',
        message: 'No Sale/Purchase module is currently open for the Agent to operate on.',
      })
      continue
    }

    const bridge = deps.getBridge(module)
    if (!bridge) {
      results.push({
        action,
        status: 'blocked',
        message: `Module ${module} is not ready for Agent actions on the current screen.`,
      })
      continue
    }

    try {
      let message = 'Done'
      switch (action.type) {
        case 'OPEN_FORM':
          message = await bridge.openForm((action.payload?.mode as 'new' | 'copy' | 'edit') ?? 'new')
          break
        case 'SELECT_ROW':
          message = await bridge.selectRow((action.payload?.selector as string | number) ?? '')
          break
        case 'SET_FIELD':
          message = await bridge.setField(String(action.payload?.field ?? ''), action.payload?.value)
          break
        case 'SAVE_FORM':
          message = await bridge.saveForm()
          break
        case 'CANCEL_FORM':
          message = await bridge.cancelForm()
          break
        case 'GET_STATE':
          message = await bridge.getState()
          break
      }
      results.push({ action, status: 'success', message })
    } catch (error) {
      results.push({
        action,
        status: 'blocked',
        message: error instanceof Error ? error.message : 'Action failed.',
      })
    }
  }

  return results
}
